import type { CancelSaveMode } from '../api/booking/bookingWriteApiSlice';

// What the operator is told after a successful cancel, in ONE place.
//
// Six surfaces cancel bookings (four detail pages, the series page, the series
// shorten panel) and each worded the outcome itself. A per-screen copy of this
// branching is how one of them ends up reporting a mail that never left as a
// plain success.
//
// Deliberately a module of its own rather than part of `cancelBookingFlow`:
// this is a pure rule, and importing it must not drag the Redux store — and
// through it Auth0 — into a caller or a test.

interface CancelOutcomeKeys {
    plain: string;
    emailSent: string;
    emailFailed: string;
}

// The single-booking and series screens word the same three outcomes
// differently ("die Buchung" vs "alle Buchungen der Serie"), so the wording
// varies by surface while the rule that picks between them does not.
export const CANCEL_MESSAGE_KEYS: Record<'booking' | 'series' | 'decline', CancelOutcomeKeys> = {
    booking: {
        plain: 'booking_details.cancel_booking.success_message',
        emailSent: 'booking_details.cancel_booking.success_message_email_sent',
        emailFailed: 'booking_details.cancel_booking.success_message_email_failed',
    },
    series: {
        plain: 'series_booking_details.cancel_success',
        emailSent: 'series_booking_details.cancel_success_email_sent',
        emailFailed: 'series_booking_details.cancel_success_email_failed',
    },
    // M8 approval (#1809): a decline is a cancel with its own mail.
    decline: {
        plain: 'booking_details.decline_booking.success_message',
        emailSent: 'booking_details.decline_booking.success_message_email_sent',
        emailFailed: 'booking_details.decline_booking.success_message_email_failed',
    },
};

/**
 * `mailSent` is the backend's account of what happened, not what was asked
 * for — a use case with no cancellation template, a contact row without a
 * usable address, or a failed send all arrive as `false` after the operator
 * explicitly asked for a mail. That has to reach the screen as a warning
 * rather than as silence.
 */
export const cancelOutcomeMessage = (
    saveMode: CancelSaveMode,
    mailSent: boolean,
    keys: CancelOutcomeKeys = CANCEL_MESSAGE_KEYS.booking,
): { messageKey: string, severity: 'success' | 'warning' } => {
    if (saveMode === 'save_only') {
        return { messageKey: keys.plain, severity: 'success' };
    }
    return mailSent
        ? { messageKey: keys.emailSent, severity: 'success' }
        : { messageKey: keys.emailFailed, severity: 'warning' };
};
